/**
 * useNavigateToEntity — generic navigation for opening any CRM entity.
 *
 * Some entity types are NEVER first-class slideouts in the product —
 * they exist only INSIDE a parent entity's slideout (notes belong to a
 * contact / deal / company; research belongs to a meeting; etc.). When
 * the user clicks one of these "sub-entity" types from the calendar, a
 * list, or anywhere else, we want to:
 *
 *  1. Resolve the sub-entity's PRIMARY parent association.
 *  2. Open the parent's slideout instead of the sub-entity directly.
 *  3. Set a URL param so the parent slideout can scroll/highlight the
 *     specific sub-entity that was clicked.
 *
 * Behavior is keyed off `SUB_ENTITY_NAV_REGISTRY`: types listed there
 * get the parent-resolution treatment; types not listed open directly
 * (current behavior preserved). Adding a new sub-entity type is a
 * single registry entry.
 *
 * Resolution order for a registered sub-entity type:
 *  1. The first association whose target type is NOT in
 *     `excludeTargetEntityTypes` (the "real" parent — typically a
 *     contact / deal / company).
 *  2. Otherwise, the entity's own slideout. We deliberately do NOT
 *     fall back to user associations — opening the owner's profile
 *     when a note has no other parent is more disruptive than just
 *     showing the note itself.
 */

import { useCallback } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { ensureEntity } from '@/lib/crm/object-cards/entityFetchBatcher';
import { getObject } from '@/lib/crm/store';
import type { CrmObject } from '@/types/crm';

import { useAssociationTypes } from './useAssociationTypes';
import { FOCUSED_ASSOCIATION_ID_PARAM } from './useFocusedAssociationScroll';
import { useOpenSlideout } from './useOpenSlideout';

/**
 * Per-type configuration for the sub-entity navigation pattern.
 */
export interface SubEntityNavEntry {
  /**
   * Entity types whose associations should be SKIPPED when looking
   * for the parent slideout. For notes this is `ahoy_user` — we
   * never want to redirect a note click to the owner's profile;
   * better to open the note's own slideout when no non-user
   * association exists.
   */
  excludeTargetEntityTypes: readonly string[];
}

/**
 * Registry mapping sub-entity types to their navigation config.
 * Entity types NOT listed here open directly via `pushSlideout`.
 *
 * Add a new entry here to onboard another sub-entity type (e.g.
 * `ahoy_research`) — no other code change required. The URL param
 * used for scroll-to-focus is global (`focusedAssociationId`), so any
 * slideout section can opt in by stamping
 * `data-focused-association-id` on its rows; see
 * `useFocusedAssociationScroll`.
 *
 * `ahoy_note` was previously registered here so calendar clicks resolved
 * to the note's parent record (contact/deal/company) — that pattern made
 * sense when notes only existed inline within other entities. Notes are
 * now first-class records (`/crm/ahoy-note/:recordId` slideout + nav item),
 * so a calendar click should open the note's own slideout. The registry
 * shape is preserved for future sub-entity types that genuinely belong to
 * a parent — keeping it empty doesn't change navigation for any other
 * type (they already fall through to the direct-open branch).
 */
export const SUB_ENTITY_NAV_REGISTRY = {} satisfies Record<
  string,
  SubEntityNavEntry
>;

interface NavigateOptions {
  /**
   * If the entity isn't already in the store, optionally fetch it
   * via the entity-fetch batcher before resolving. Defaults to true.
   * Set false to preserve a synchronous fallback (push the entity id)
   * when the parent isn't yet known.
   */
  fetchIfMissing?: boolean;
}

/**
 * Pick the best slideout target from a sub-entity's associations,
 * keyed off the registry config for that entity type.
 *
 * Returns the entity_id to push, or null if the sub-entity has no
 * resolvable association at all (caller should fall back to the
 * sub-entity's own id).
 */
export function resolveSubEntityTarget(
  subEntityObject: CrmObject | undefined,
  associationTypes: AssociationType[],
  config: SubEntityNavEntry
): string | null {
  if (
    !subEntityObject?.associations ||
    subEntityObject.associations.length === 0
  ) {
    return null;
  }
  const targetTypeFor = (assocTypeName: string): string | undefined =>
    associationTypes.find(t => t.association_type_name === assocTypeName)
      ?.target_entity_type_name;
  const isExcluded = (assocTypeName: string): boolean => {
    const target = targetTypeFor(assocTypeName);
    // Unknown target (association types haven't loaded yet, or this
    // type isn't in the schema) — treat as excluded so we don't
    // redirect to a "parent" we can't verify isn't an excluded type
    // (e.g. owner user). Caller falls back to the sub-entity's own
    // slideout, which is safer than landing on the wrong target.
    if (!target) {
      return true;
    }
    return config.excludeTargetEntityTypes.includes(target);
  };

  // Find the first association whose target type is NOT excluded.
  // Excluded targets (e.g. user for notes) are skipped entirely — we
  // do NOT fall back to opening them when no other association exists;
  // the caller opens the sub-entity's own slideout instead.
  const primary = subEntityObject.associations.find(
    a => !isExcluded(a.association_type_name)
  );
  return primary?.target_entity_id ?? null;
}

/**
 * Hook returning a navigate function. Call site only needs to provide
 * `entityId` and `entityTypeName`; the registry handles the rest.
 *
 * @example
 *   const navigate = useNavigateToEntity();
 *   navigate(event.id, event.entity_type_name);
 */
export function useNavigateToEntity() {
  // Origin-aware open: layers only when the click comes from within a
  // rendered entity/thread slideout; replaces otherwise (the product rule).
  const openSlideout = useOpenSlideout();
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { associationTypes } = useAssociationTypes(orgId);

  return useCallback(
    (entityId: string, entityTypeName?: string, options?: NavigateOptions) => {
      const config = entityTypeName
        ? SUB_ENTITY_NAV_REGISTRY[
            entityTypeName as keyof typeof SUB_ENTITY_NAV_REGISTRY
          ]
        : undefined;

      // Not a registered sub-entity — open directly. This keeps every
      // existing call site (meetings, contacts, deals, etc.) working
      // without a list of "exclusion" types to maintain.
      if (!config) {
        openSlideout(entityId);
        return;
      }

      const fetchIfMissing = options?.fetchIfMissing ?? true;
      const subEntityObject = getObject(entityId);
      const target = resolveSubEntityTarget(
        subEntityObject,
        associationTypes,
        config
      );

      // Sub-entity not in the store yet OR has no associations to
      // resolve from. Fall back to the entity's own slideout, but
      // kick off a fetch so a future click resolves correctly.
      if (!target) {
        if (fetchIfMissing && orgId && !subEntityObject) {
          ensureEntity(orgId, entityId);
        }
        openSlideout(entityId);
        return;
      }

      openSlideout(target, {
        searchParams: { [FOCUSED_ASSOCIATION_ID_PARAM]: entityId },
      });
    },
    [openSlideout, orgId, associationTypes]
  );
}
